import React, { useState, useRef } from 'react';
import { Button, Table, Input, Select, Modal, Space, message, Card, Tag, Progress, Tooltip } from 'antd';
import { 
    UploadOutlined, 
    PlusOutlined, 
    DeleteOutlined, 
    CheckCircleOutlined, 
    CloseCircleOutlined,
    InfoCircleOutlined 
} from '@ant-design/icons';
import { massSubmitRows } from '../../api';
import * as XLSX from 'xlsx';

const { Option } = Select;

interface RowData {
    registrationNumber: string;
    registrationEmail: string;
    tourInfoCode?: string;
    contactListCode?: string;
    itineraryCode?: string;
    accommodationCode?: string;
    status?: 'pending' | 'success' | 'error';
    message?: string;
}

export default function MassSubmitPage() {
    const [rows, setRows] = useState<RowData[]>([]);
    const [loading, setLoading] = useState(false);
    const [concurrency, setConcurrency] = useState(5);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [progress, setProgress] = useState<{ current: number; total: number } | null>(null);
    const [sectionCodes, setSectionCodes] = useState<any[]>([]);

    const inputRef = useRef<HTMLInputElement | null>(null);

    React.useEffect(() => {
        loadSectionCodes();
    }, []);

    const loadSectionCodes = async () => {
        try {
            const { fetchSectionCodes } = await import('../../api');
            const resp = await fetchSectionCodes();
            const result = await resp.json();
            if (result.success) {
                setSectionCodes(result.data || []);
            }
        } catch (err) {
            console.error('Failed to load section codes', err);
        }
    };

    const addRow = () => {
        setRows([...rows, { 
            registrationNumber: '', 
            registrationEmail: '',
            status: 'pending'
        }]);
    };

    const updateCell = (index: number, key: string, value: any) => {
        const copy = [...rows];
        copy[index] = { ...copy[index], [key]: value };
        setRows(copy);
    };

    const removeSelectedRows = () => {
        const newRows = rows.filter((_, index) => !selectedRowKeys.includes(index));
        setRows(newRows);
        setSelectedRowKeys([]);
        message.success(`已刪除 ${selectedRowKeys.length} 行`);
    };

    const onFile = async (file: File) => {
        try {
            const ab = await file.arrayBuffer();
            const wb = XLSX.read(ab, { type: 'array' });
            const sheetName = wb.SheetNames[0];
            const sheet = wb.Sheets[sheetName];
            const json = XLSX.utils.sheet_to_json<any>(sheet, { defval: '' });
            
            const mapped = json.map((row: any) => {
                const lowerKeys: any = {};
                for (const k of Object.keys(row)) lowerKeys[k.toLowerCase().trim()] = row[k];
                return {
                    registrationNumber: row.registrationNumber || row.registration_number || lowerKeys['registrationnumber'] || lowerKeys['registration_number'] || lowerKeys['regno'] || row['RegNo'] || row['regNo'] || '',
                    registrationEmail: row.registrationEmail || row.registration_email || lowerKeys['registrationemail'] || lowerKeys['registration_email'] || lowerKeys['email'] || row['Email'] || '',
                    tourInfoCode: row.tourInfoCode || lowerKeys['tourinfocode'] || row['Tour-Code'] || '',
                    contactListCode: row.contactListCode || lowerKeys['contactlistcode'] || '',
                    itineraryCode: row.itineraryCode || lowerKeys['itinerarycode'] || '',
                    accommodationCode: row.accommodationCode || lowerKeys['accommodationcode'] || '',
                    status: 'pending' as const,
                };
            });
            setRows([...rows, ...mapped]);
            message.success(`已匯入 ${mapped.length} 筆記錄`);
        } catch (err) {
            console.error(err);
            message.error('檔案解析失敗');
        }
    };

    const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0];
        if (f) onFile(f);
        e.currentTarget.value = '';
    };

    const validateRows = () => {
        const errors: string[] = [];
        rows.forEach((row, index) => {
            if (!row.registrationNumber) {
                errors.push(`第 ${index + 1} 行缺少註冊編號`);
            }
            if (!row.registrationEmail) {
                errors.push(`第 ${index + 1} 行缺少註冊電郵`);
            }
        });
        return errors;
    };

    const submitAll = async () => {
        if (rows.length === 0) {
            message.warning('請先添加至少一筆記錄');
            return;
        }

        const errors = validateRows();
        if (errors.length > 0) {
            Modal.error({
                title: '資料驗證失敗',
                content: (
                    <div>
                        {errors.map((err, i) => <div key={i}>• {err}</div>)}
                    </div>
                ),
            });
            return;
        }

        Modal.confirm({
            title: '確認批次提交',
            content: `確定要提交 ${rows.length} 筆記錄嗎？`,
            okText: '確認提交',
            cancelText: '取消',
            onOk: async () => {
                setLoading(true);
                setProgress({ current: 0, total: rows.length });
                
                try {
                    const resp = await massSubmitRows(rows, { concurrency, dryRun: false });
                    const result = await resp.json();
                    
                    if (result.success && result.results) {
                        const updatedRows = [...rows];
                        result.results.forEach((res: any, idx: number) => {
                            updatedRows[idx] = {
                                ...updatedRows[idx],
                                status: res.ok ? 'success' : 'error',
                                message: res.ok ? `成功 - ${res.referenceNumber || ''}` : res.error
                            };
                        });
                        setRows(updatedRows);
                        
                        const successCount = result.results.filter((r: any) => r.ok).length;
                        const failCount = result.results.length - successCount;
                        
                        Modal.success({
                            title: '批次提交完成',
                            content: (
                                <div>
                                    <p>成功: {successCount} 筆</p>
                                    <p>失敗: {failCount} 筆</p>
                                </div>
                            ),
                        });
                    } else {
                        message.error(result.message || '批次提交失敗');
                    }
                } catch (err) {
                    console.error(err);
                    message.error('批次提交發生錯誤');
                } finally {
                    setLoading(false);
                    setProgress(null);
                }
            }
        });
    };

    const exportTemplate = () => {
        const template = [
            {
                registrationNumber: 'RN-001',
                registrationEmail: 'example@email.com',
                tourInfoCode: 'TOUR-001',
                contactListCode: 'CONTACT-001',
                itineraryCode: 'ITIN-001',
                accommodationCode: 'ACC-001',
            }
        ];
        
        const ws = XLSX.utils.json_to_sheet(template);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Template');
        XLSX.writeFile(wb, '批次提交範本.xlsx');
        message.success('範本已下載');
    };

    const columns: any = [
        { 
            title: '註冊編號', 
            dataIndex: 'registrationNumber', 
            width: 150,
            render: (v: any, r: any, i: number) => (
                <Input 
                    value={v} 
                    onChange={(e) => updateCell(i, 'registrationNumber', e.target.value)}
                    placeholder="必填"
                    status={!v ? 'error' : ''}
                />
            )
        },
        { 
            title: '註冊電郵', 
            dataIndex: 'registrationEmail', 
            width: 180,
            render: (v: any, r: any, i: number) => (
                <Input 
                    value={v} 
                    onChange={(e) => updateCell(i, 'registrationEmail', e.target.value)}
                    placeholder="必填"
                    status={!v ? 'error' : ''}
                />
            )
        },
        { 
            title: '旅行團資料代碼', 
            dataIndex: 'tourInfoCode', 
            width: 150,
            render: (v: any, r: any, i: number) => (
                <Select
                    showSearch
                    value={v}
                    onChange={(val) => updateCell(i, 'tourInfoCode', val)}
                    placeholder="選擇代碼"
                    style={{ width: '100%' }}
                    allowClear
                    filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    options={sectionCodes
                        .filter(code => code.type === 'tourInfo')
                        .map(code => ({ value: code.code, label: `${code.code} - ${code.name || ''}` }))}
                />
            )
        },
        { 
            title: '聯絡人代碼', 
            dataIndex: 'contactListCode', 
            width: 150,
            render: (v: any, r: any, i: number) => (
                <Select
                    showSearch
                    value={v}
                    onChange={(val) => updateCell(i, 'contactListCode', val)}
                    placeholder="選擇代碼"
                    style={{ width: '100%' }}
                    allowClear
                    filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    options={sectionCodes
                        .filter(code => code.type === 'contactList')
                        .map(code => ({ value: code.code, label: `${code.code} - ${code.name || ''}` }))}
                />
            )
        },
        { 
            title: '行程代碼', 
            dataIndex: 'itineraryCode', 
            width: 150,
            render: (v: any, r: any, i: number) => (
                <Select
                    showSearch
                    value={v}
                    onChange={(val) => updateCell(i, 'itineraryCode', val)}
                    placeholder="選擇代碼"
                    style={{ width: '100%' }}
                    allowClear
                    filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    options={sectionCodes
                        .filter(code => code.type === 'itinerary')
                        .map(code => ({ value: code.code, label: `${code.code} - ${code.name || ''}` }))}
                />
            )
        },
        { 
            title: '住宿代碼', 
            dataIndex: 'accommodationCode', 
            width: 150,
            render: (v: any, r: any, i: number) => (
                <Select
                    showSearch
                    value={v}
                    onChange={(val) => updateCell(i, 'accommodationCode', val)}
                    placeholder="選擇代碼"
                    style={{ width: '100%' }}
                    allowClear
                    filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    options={sectionCodes
                        .filter(code => code.type === 'accommodation')
                        .map(code => ({ value: code.code, label: `${code.code} - ${code.name || ''}` }))}
                />
            )
        },
        {
            title: '狀態',
            dataIndex: 'status',
            width: 100,
            fixed: 'right' as const,
            render: (status: string, record: RowData) => {
                if (!status || status === 'pending') {
                    return <Tag color="default">待提交</Tag>;
                }
                if (status === 'success') {
                    return (
                        <Tooltip title={record.message}>
                            <Tag icon={<CheckCircleOutlined />} color="success">成功</Tag>
                        </Tooltip>
                    );
                }
                if (status === 'error') {
                    return (
                        <Tooltip title={record.message}>
                            <Tag icon={<CloseCircleOutlined />} color="error">失敗</Tag>
                        </Tooltip>
                    );
                }
                return null;
            }
        },
    ];

    const rowSelection = {
        selectedRowKeys,
        onChange: (keys: React.Key[]) => setSelectedRowKeys(keys),
    };

    return (
        <div style={{ 
            padding: '24px',
            backgroundColor: '#f5f7fa',
            minHeight: 'calc(100vh - 80px)'
        }}>
            <Card 
                title={
                    <Space>
                        <span style={{ fontSize: '18px', fontWeight: 600 }}>批次提交</span>
                        <Tooltip title="使用區段代碼快速填充多筆提交記錄">
                            <InfoCircleOutlined style={{ color: '#999' }} />
                        </Tooltip>
                    </Space>
                }
                bordered={false}
                style={{ 
                    boxShadow: '0 1px 2px 0 rgba(0,0,0,0.03), 0 1px 6px -1px rgba(0,0,0,0.02), 0 2px 4px 0 rgba(0,0,0,0.02)'
                }}
            >
                <Space 
                    style={{ 
                        marginBottom: 16, 
                        width: '100%', 
                        justifyContent: 'space-between',
                        flexWrap: 'wrap'
                    }}
                >
                    <Space wrap>
                        <Button 
                            icon={<PlusOutlined />} 
                            onClick={addRow}
                        >
                            新增行
                        </Button>
                        <input 
                            ref={inputRef} 
                            type="file" 
                            accept=".csv,.xlsx,.xls" 
                            style={{ display: 'none' }} 
                            onChange={handleFileInput} 
                        />
                        <Button 
                            icon={<UploadOutlined />} 
                            onClick={() => inputRef.current?.click()}
                        >
                            匯入檔案
                        </Button>
                        <Button onClick={exportTemplate}>
                            下載範本
                        </Button>
                        {selectedRowKeys.length > 0 && (
                            <Button 
                                danger 
                                icon={<DeleteOutlined />}
                                onClick={removeSelectedRows}
                            >
                                刪除選中 ({selectedRowKeys.length})
                            </Button>
                        )}
                    </Space>
                    <Space>
                        <span style={{ color: '#666' }}>並發數:</span>
                        <Select 
                            value={concurrency} 
                            onChange={(v) => setConcurrency(v)} 
                            style={{ width: 100 }}
                        >
                            {[1,2,3,4,5,6,7,8,9,10].map(n => (
                                <Option key={n} value={n}>{n}</Option>
                            ))}
                        </Select>
                        <Button 
                            type="primary" 
                            onClick={submitAll} 
                            loading={loading}
                            disabled={rows.length === 0}
                        >
                            提交全部 ({rows.length})
                        </Button>
                    </Space>
                </Space>

                {progress && (
                    <div style={{ marginBottom: 16 }}>
                        <Progress 
                            percent={Math.round((progress.current / progress.total) * 100)} 
                            status="active"
                            format={() => `${progress.current} / ${progress.total}`}
                        />
                    </div>
                )}

                <Table 
                    rowKey={(r, i) => String(i)} 
                    rowSelection={rowSelection}
                    dataSource={rows} 
                    columns={columns}
                    scroll={{ x: 1400 }}
                    pagination={{
                        showSizeChanger: true,
                        showTotal: (total) => `共 ${total} 筆記錄`,
                        defaultPageSize: 20,
                    }}
                    locale={{
                        emptyText: '暫無資料，請點擊「新增行」或「匯入檔案」開始'
                    }}
                />
            </Card>
        </div>
    );
}
